import React, { useMemo, type PropsWithChildren, useEffect } from "react";
import { RequestError, UserProvider } from "@auth0/nextjs-auth0";
import NextApp from "next/app";
import Head from "next/head";
import Script from "next/script";
import { Inter } from "next/font/google";

import {
  lightTheme,
  darkTheme,
  lightVETheme,
  darkVETheme,
  globalStylesWithTheme,
} from "@puzzle/theme";
import { withApollo } from "apollo/withApollo";
import { config, IS_SERVER } from "lib/config";
import { PuzzleIntercomProvider } from "lib/customerSupport/PuzzleIntercomProvider";
import { Route } from "lib/routes";
import { ErrorBoundary } from "lib/errors/errors";
import { initHighlight } from "lib/errors/highlight";
import { usePageLoadTrace } from "lib/instrumentation/usePageLoadTrace";
import { initBugsnag } from "lib/errors/bugsnag";
import { initDatadogRum } from "lib/instrumentation/dataDogRUM";
import { GtmScript, GtagScript } from "lib/analytics/google";

import { AppProviders } from "domains/app/AppProviders/AppProviders";
import { ThemeProvider } from "components/common/theme/ThemeProvider";
import { ExtendedNextApp } from "components/common/types";
import { InactivityTimer } from "components/common/InactivityTimer";
import { useTheme } from "components/common/hooks/useTheme/useTheme";
import { HTTP_STATUS_CODES } from "constants/httpStatusCodes";
import { MODAL_ROOT_ID } from "constants/ui";

const PLAID_LINK_SCRIPT_URL = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  display: "swap",
  fallback: ["Roboto", "sans-serif"],
  variable: "--fonts-inter",
});

// Performance and Error Monitoring
initBugsnag();
initHighlight();
initDatadogRum();

const WithAuth = ({ children }: PropsWithChildren<unknown>) => {
  const userFetcher = async (url: string) => {
    try {
      const response = await fetch(url);
      if (response.ok) {
        return response.json();
      } else if (response.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
        window.location.href = "/login";
        return undefined; // Unauthorized
      }
      throw new RequestError(response.status);
    } catch {
      throw new RequestError(0); // Network error
    }
  };

  return (
    <UserProvider profileUrl={Route.me} fetcher={userFetcher} loginUrl={Route.login}>
      <InactivityTimer />
      {children}
    </UserProvider>
  );
};

const ModalRoot = () => {
  return <div id={MODAL_ROOT_ID} />;
};

const App: ExtendedNextApp = ({ Component, pageProps }) => {
  const { mode } = useTheme();

  usePageLoadTrace();
  const hasEnabledGoogleTagManager = !!(config.GTM_ID && config.GTM_ENV_PARAMS);

  const content = useMemo(() => {
    const getLayout = Component.getLayout || ((page) => page);
    const result = <main>{getLayout(<Component {...pageProps} />)}</main>;

    return Component.disableTheme ? result : <ThemeProvider>{result}</ThemeProvider>;
  }, [Component, pageProps]);

  const Noop = ({ children }: React.PropsWithChildren<unknown>) => <>{children}</>;
  const MainWrapper = Component.disableAuthAndDataProviders ? Noop : WithAuth;

  useEffect(() => {
    globalStylesWithTheme(mode);
  }, [mode]);

  useEffect(() => {
    if (IS_SERVER) return;

    const root = document.documentElement;
    root.classList.remove(lightVETheme, darkVETheme, lightTheme, darkTheme);

    if (mode === "light") {
      root.classList.add(lightVETheme, lightTheme);
    } else {
      root.classList.add(darkVETheme, darkTheme);
    }
  }, [mode]);

  return (
    <div className={inter.variable}>
      <ErrorBoundary>
        <PuzzleIntercomProvider shouldInitialize={!Component.disableIntercom}>
          <MainWrapper>
            <Head>
              {/** https://err.sh/next.js/no-document-viewport-meta */}
              <meta name="viewport" content="width=device-width, initial-scale=1.0" />
              <title>Puzzle</title>
            </Head>
            {hasEnabledGoogleTagManager && (
              <>
                <GtmScript id={config.GTM_ID} env={config.GTM_ENV_PARAMS} />
                <GtagScript id={config.GTM_ID} />
              </>
            )}
            {/* Sometimes react-plaid-link doesn't load the script: https://github.com/plaid/react-plaid-link/issues/86 */}
            <Script src={PLAID_LINK_SCRIPT_URL} strategy="beforeInteractive" />
            <AppProviders
              disableDataProviders={
                !!Component.disableDataProviders || !!Component.disableAuthAndDataProviders
              }
            >
              {content}
            </AppProviders>
            <ModalRoot />
          </MainWrapper>
        </PuzzleIntercomProvider>
      </ErrorBoundary>
    </div>
  );
};

// Next.js seems to ignore the getInitialProps from withApollo.
// This no-op intentionally disables static page generation.
App.getInitialProps = async (appContext) => {
  const appProps = await NextApp.getInitialProps(appContext);

  return { ...appProps };
};

export default withApollo({ ssr: false })(App);
